import { useEffect, useRef, useMemo, useCallback, type ChangeEvent, type ReactNode } from "react";
import "../../../assets/fa-icons/css/fontawesome.css";
import "../../../assets/fa-icons/css/sharp-regular.css";
import "../../../assets/fa-icons/css/sharp-solid.css";
import { FontStyles } from "../../utils/font-styles";
import { cn } from "../../utils/cn";

interface CheckboxProps {
  /** Whether the checkbox is checked */
  checked: boolean;
  /**
   * Handler called when the checkbox state changes.
   * Omit this prop when:
   * - Using the checkbox as display-only (read-only state indicator)
   * - The checkbox is controlled externally (e.g., clicked via parent onClick handler)
   * - The checkbox is part of a form controlled by a form library (e.g., React Hook Form)
   */
  onChange?: (checked: boolean) => void;
  /** Unique identifier for the checkbox input element */
  id: string;
  /** Optional label text or React node displayed next to the checkbox */
  label?: string | ReactNode;
  /** Whether the checkbox is disabled */
  disabled?: boolean;
  /** Whether to display the indeterminate state (dash icon) */
  indeterminate?: boolean;
  /** Optional name attribute for the checkbox input element */
  name?: string;
  /** Additional CSS classes to apply to the checkbox wrapper */
  className?: string;
}

// Static styles - defined once outside component
const BASE_BOX_STYLES =
  "w-5 h-5 rounded flex items-center justify-center border border-solid p-1 transition-colors shrink-0";
const ICON_BASE_STYLES = "fa-sharp text-neutral-white text-[12px] leading-none fa-fw";
const ICON_CHECK_STYLES = "fa-regular fa-check";
const ICON_INDETERMINATE_STYLES = "fa-dash fa-solid text-[10px]";
const FOCUS_RING_STYLES = "peer-focus-visible:shadow-outline-2 peer-focus-visible:shadow-loch-100";
const LABEL_TEXT_STYLES = cn(
  FontStyles["body-md"],
  "font-medium text-slate tracking-normal leading-none md:leading-none select-none cursor-pointer",
);

export const Checkbox = ({
  checked,
  onChange,
  label,
  disabled = false,
  indeterminate = false,
  id,
  name,
  className = "",
}: CheckboxProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle indeterminate state (requires direct DOM manipulation)
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  // Memoize change handler to avoid recreation on every render
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!disabled && onChange) {
        onChange(e.target.checked);
      }
    },
    [disabled, onChange],
  );

  // Handle click on checkbox visual to trigger input
  const handleBoxClick = useCallback(() => {
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  }, [disabled]);

  // Memoize computed styles based on state
  const checkboxBoxStyles = useMemo(() => {
    const isCheckedOrIndeterminate = checked || indeterminate;

    if (isCheckedOrIndeterminate) {
      return disabled
        ? cn(BASE_BOX_STYLES, "bg-loch-200 border-loch-200")
        : cn(BASE_BOX_STYLES, "bg-loch border-loch hover:bg-loch-700 hover:border-loch-700", FOCUS_RING_STYLES);
    }

    return disabled
      ? cn(BASE_BOX_STYLES, "bg-neutral-white border-foggy-firth")
      : cn(BASE_BOX_STYLES, "bg-neutral-white border-foggy-firth hover:border-neutral-200", FOCUS_RING_STYLES);
  }, [checked, indeterminate, disabled]);

  // Memoize icon styles
  const iconStyles = useMemo(() => {
    const isCheckedOrIndeterminate = checked || indeterminate;
    const variantStyles = indeterminate ? ICON_INDETERMINATE_STYLES : ICON_CHECK_STYLES;
    const visibilityStyles = !isCheckedOrIndeterminate ? "opacity-0" : "";

    return cn(ICON_BASE_STYLES, variantStyles, visibilityStyles);
  }, [checked, indeterminate]);

  // Memoize label wrapper styles
  const labelWrapperStyles = useMemo(() => {
    return cn("inline-flex items-center gap-2 cursor-pointer", disabled && "opacity-70", className);
  }, [disabled, className]);

  return (
    <div className={labelWrapperStyles}>
      <div
        onClick={handleBoxClick}
        className="relative inline-flex items-center justify-center cursor-pointer"
        role="presentation"
      >
        {/* Hidden native checkbox for accessibility */}
        <input
          ref={inputRef}
          type="checkbox"
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          id={id}
          name={name}
          aria-checked={indeterminate ? "mixed" : checked}
          className="absolute opacity-0 w-0 h-0 peer"
        />

        {/* Custom checkbox visual - padding 4px inside */}
        <div className={checkboxBoxStyles}>
          <i className={iconStyles} aria-hidden="true" />
        </div>
      </div>

      {/* Label text - single label element for accessibility */}
      {label && (
        <label htmlFor={id} className={LABEL_TEXT_STYLES}>
          {label}
        </label>
      )}
    </div>
  );
};
